import { GameState, Position, Player, GameMode, GameType, REVERSI_CONFIG } from '../types/game';

// 创建初始黑白棋游戏状态
export const createInitialReversiState = (gameMode: GameMode, difficulty?: 'easy' | 'medium' | 'hard'): GameState => {
  const board: (Player | null)[][] = Array(8).fill(null).map(() => Array(8).fill(null));

  // 初始化中央4个棋子
  board[3][3] = 'white';
  board[3][4] = 'black';
  board[4][3] = 'black';
  board[4][4] = 'white';

  const validMoves = getValidMoves(board, 'black');

  return {
    board,
    currentPlayer: 'black',
    status: 'playing',
    winner: null,
    moves: [],
    energy: { black: 0, white: 0 },
    skills: { black: [], white: [] },
    gameHistory: [],
    gameMode,
    gameType: 'reversi',
    difficulty,
    isAIThinking: false,
    validMoves,
    lastFlippedPieces: [],
    passCount: 0,
    blackScore: 2,
    whiteScore: 2
  };
};

// 检查位置是否在棋盘内
const isValidPosition = (position: Position, boardSize: number = 8): boolean => {
  return position.row >= 0 && position.row < boardSize &&
         position.col >= 0 && position.col < boardSize;
};

// 检查位置是否为空
const isEmpty = (board: (Player | null)[][], position: Position): boolean => {
  return board[position.row][position.col] === null;
};

// 获取某个方向上可以被翻转的棋子
const getFlippedPiecesInDirection = (
  board: (Player | null)[][],
  position: Position,
  player: Player,
  direction: [number, number]
): Position[] => {
  const [dx, dy] = direction;
  const opponent = player === 'black' ? 'white' : 'black';
  const flippedPieces: Position[] = [];

  let row = position.row + dx;
  let col = position.col + dy;

  // 首先必须遇到对手的棋子
  if (!isValidPosition({ row, col }, 8) || board[row][col] !== opponent) {
    return [];
  }

  flippedPieces.push({ row, col });
  row += dx;
  col += dy;

  // 继续查找，直到遇到自己的棋子或边界
  while (isValidPosition({ row, col }, 8)) {
    const currentPiece = board[row][col];

    if (currentPiece === null) {
      // 遇到空位，无法翻转
      return [];
    } else if (currentPiece === player) {
      // 遇到自己的棋子，可以翻转中间的所有对手棋子
      return flippedPieces;
    } else {
      // 遇到对手的棋子，继续查找
      flippedPieces.push({ row, col });
      row += dx;
      col += dy;
    }
  }

  // 到达边界但没有遇到自己的棋子，无法翻转
  return [];
};

// 获取某个位置可以翻转的所有棋子
const getAllFlippedPieces = (
  board: (Player | null)[][],
  position: Position,
  player: Player
): Position[] => {
  if (!isEmpty(board, position)) {
    return [];
  }

  const directions: [number, number][] = [
    [-1, -1], [-1, 0], [-1, 1],
    [0, -1],           [0, 1],
    [1, -1],  [1, 0],  [1, 1]
  ];

  const allFlippedPieces: Position[] = [];

  for (const direction of directions) {
    const flippedPieces = getFlippedPiecesInDirection(board, position, player, direction);
    allFlippedPieces.push(...flippedPieces);
  }

  return allFlippedPieces;
};

// 检查某个位置是否为有效移动
export const isValidMove = (
  board: (Player | null)[][],
  position: Position,
  player: Player
): boolean => {
  return getAllFlippedPieces(board, position, player).length > 0;
};

// 获取当前玩家所有有效的移动位置
export const getValidMoves = (
  board: (Player | null)[][],
  player: Player
): Position[] => {
  const validMoves: Position[] = [];

  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const position = { row, col };
      if (isValidMove(board, position, player)) {
        validMoves.push(position);
      }
    }
  }

  return validMoves;
};

// 执行移动
export const makeReversiMove = (gameState: GameState, position: Position): GameState => {
  const { board, currentPlayer } = gameState;

  // 检查移动是否有效
  if (!isValidMove(board, position, currentPlayer)) {
    return gameState;
  }

  // 获取要翻转的棋子
  const flippedPieces = getAllFlippedPieces(board, position, currentPlayer);

  if (flippedPieces.length === 0) {
    return gameState;
  }

  // 创建新棋盘
  const newBoard = board.map(row => [...row]);

  // 放置新棋子
  newBoard[position.row][position.col] = currentPlayer;

  // 翻转棋子
  for (const piece of flippedPieces) {
    newBoard[piece.row][piece.col] = currentPlayer;
  }

  // 计算新的分数
  const { blackScore, whiteScore } = calculateScore(newBoard);

  // 切换玩家
  const nextPlayer = currentPlayer === 'black' ? 'white' : 'black';

  // 获取下一个玩家的有效移动
  const nextValidMoves = getValidMoves(newBoard, nextPlayer);

  // 创建移动记录
  const newMove = {
    position,
    player: currentPlayer,
    timestamp: Date.now(),
  };

  const newState: GameState = {
    ...gameState,
    board: newBoard,
    currentPlayer: nextPlayer,
    moves: [...gameState.moves, newMove],
    validMoves: nextValidMoves.length > 0 ? nextValidMoves : [],
    lastFlippedPieces: flippedPieces,
    blackScore,
    whiteScore,
    passCount: 0, // 重置跳过计数
    gameHistory: [...gameState.gameHistory, gameState]
  };

  // 检查游戏是否结束
  return checkGameEnd(newState);
};

// 计算分数
export const calculateScore = (board: (Player | null)[][]): { blackScore: number; whiteScore: number } => {
  let blackScore = 0;
  let whiteScore = 0;

  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      if (board[row][col] === 'black') {
        blackScore++;
      } else if (board[row][col] === 'white') {
        whiteScore++;
      }
    }
  }

  return { blackScore, whiteScore };
};

// 跳过回合
export const passTurn = (gameState: GameState): GameState => {
  const nextPlayer = gameState.currentPlayer === 'black' ? 'white' : 'black';
  const nextValidMoves = getValidMoves(gameState.board, nextPlayer);
  const newPassCount = (gameState.passCount || 0) + 1;

  const newState: GameState = {
    ...gameState,
    currentPlayer: nextPlayer,
    validMoves: nextValidMoves.length > 0 ? nextValidMoves : [],
    passCount: newPassCount,
    gameHistory: [...gameState.gameHistory, gameState]
  };

  // 如果连续跳过两次，游戏结束
  if (newPassCount >= 2) {
    return {
      ...newState,
      status: 'finished',
      winner: determineWinner(newState.board)
    };
  }

  return newState;
};

// 检查游戏是否结束
export const checkGameEnd = (gameState: GameState): GameState => {
  const currentPlayerMoves = getValidMoves(gameState.board, gameState.currentPlayer);
  const opponent = gameState.currentPlayer === 'black' ? 'white' : 'black';
  const opponentMoves = getValidMoves(gameState.board, opponent);

  if (currentPlayerMoves.length === 0 && opponentMoves.length === 0) {
    // 双方都无法移动，游戏结束
    const winner = determineWinner(gameState.board);
    return {
      ...gameState,
      status: 'finished',
      winner,
      validMoves: []
    };
  }

  if (currentPlayerMoves.length === 0) {
    // 当前玩家无法移动，自动跳过
    return passTurn(gameState);
  }

  return gameState;
};

// 确定获胜者
export const determineWinner = (board: (Player | null)[][]): Player | null => {
  const { blackScore, whiteScore } = calculateScore(board);

  if (blackScore > whiteScore) {
    return 'black';
  } else if (whiteScore > blackScore) {
    return 'white';
  } else {
    return null; // 平局
  }
};

// 悔棋
export const undoReversiMove = (gameState: GameState): GameState => {
  if (gameState.gameHistory.length === 0) {
    return gameState;
  }

  const previousState = gameState.gameHistory[gameState.gameHistory.length - 1];
  return {
    ...previousState,
    gameHistory: previousState.gameHistory
  };
};

// 重置游戏
export const resetReversiGame = (gameMode: GameMode, difficulty?: 'easy' | 'medium' | 'hard'): GameState => {
  return createInitialReversiState(gameMode, difficulty);
};